
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../base/Button';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../context/CartContext';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { items } = useCart();
  const isSignedIn = Boolean(user);
  const hasAdminAccess = Boolean(user?.has_admin_access || user?.is_admin);
  const displayName = user?.display_name || user?.email || 'Account';
  const artistCtaLabel = hasAdminAccess ? 'Add Your Music' : 'Become an Artist';
  const artistCtaHref = hasAdminAccess ? '/admin/releases' : '/artists/apply';
  const cartItemCount = useMemo(
    () => items.reduce((total, item) => total + item.quantity, 0),
    [items]
  );

  const handleSignOut = () => {
    signOut();
    setIsMenuOpen(false);
    navigate('/');
  };

  const handleAdmin = () => {
    setIsMenuOpen(false);
    navigate('/admin');
  };
  
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/images/254trackslogo1.png"
              alt="254Tracks"
              className="h-16 w-auto object-contain"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <a href="/explore" className="text-gray-700 hover:text-emerald-600 transition-colors">
              Explore
            </a>
            <a href="/releases" className="text-gray-700 hover:text-emerald-600 transition-colors">
              Releases
            </a>
            <a href="/artists" className="text-gray-700 hover:text-emerald-600 transition-colors">
              Artists
            </a>
            <a href="/about" className="text-gray-700 hover:text-emerald-600 transition-colors">
              About
            </a>
            <a href={artistCtaHref} className="text-gray-700 hover:text-emerald-600 transition-colors">
              {artistCtaLabel}
            </a>
            {isSignedIn && (
              <a href="/library" className="text-gray-700 hover:text-emerald-600 transition-colors">
                Library
              </a>
            )}
          </div>
          
          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {isSignedIn ? (
              <>
                <span className="text-sm text-gray-700">Hi, {displayName}</span>
                {hasAdminAccess && (
                  <Button
                    onClick={handleAdmin}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 whitespace-nowrap"
                  >
                    Admin
                  </Button>
                )}
                <Button
                  onClick={handleSignOut}
                  variant="ghost"
                  className="text-gray-700 hover:text-emerald-600 px-4 py-2"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/signin')}
                  className="text-gray-700 hover:text-emerald-600 transition-colors"
                >
                  Sign In
                </button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 whitespace-nowrap"
                >
                  Sign Up Free
                </Button>
              </>
            )}
            <Link
              to="/cart"
              aria-label="Shopping cart"
              className="relative text-gray-700 hover:text-emerald-600 transition-colors"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shopping-cart-line"></i>
              </div>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 px-1 rounded-full bg-emerald-600 text-white text-xs font-semibold flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>
          </div>
          
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg-hidden p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </div>
          </button>
        </div>
        
        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg-hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                <Link
                  to="/explore"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                >
                  Explore
                </Link>
                <Link
                  to="/releases"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                >
                  Releases
                </Link>
                <Link
                  to="/artists"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                >
                  Artists
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                >
                  About
                </Link>
                <Link
                  to={artistCtaHref}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                >
                  {artistCtaLabel}
                </Link>
                {isSignedIn && (
                  <Link
                    to="/library"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                  >
                    Library
                  </Link>
                )}
                <Link
                  to="/cart"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                >
                  Cart{cartItemCount > 0 ? ` (${cartItemCount})` : ''}
                </Link>
                {isSignedIn ? (
                  <>
                    {hasAdminAccess && (
                      <Link
                        to="#"
                        onClick={(event) => {
                          event.preventDefault();
                          handleAdmin();
                        }}
                        className="block px-3 py-2 text-gray-700 hover:text-emerald-600"
                      >
                        Admin
                      </Link>
                    )}
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-3 py-2 text-gray-700 hover:text-emerald-600"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/signin');
                      }}
                      className="block w-full text-left px-3 py-2 text-gray-700 hover:text-emerald-600"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/signup');
                      }}
                      className="block w-full text-left px-3 py-2 text-emerald-600 font-medium"
                    >
                      Sign Up Free
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="h-1 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500"></div>
    </header>
  );
};
